import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import {
  CORE_METRIC_FALLBACK_LABELS,
  CORE_METRIC_IDS,
  formatMetric,
  metricNumber,
  useMetrics,
} from "../useMetrics";
import { bucketValue, buildMetricSections } from "./metricGrid";

/**
 * Print-first consultant report. Opened in a new tab from the Reporting hub:
 *
 *   /reportprint/consultant?consultant=<userId>&start=YYYY-MM-DD&end=YYYY-MM-DD&name=<label>
 *
 * `start` / `end` are both optional (omit for all-time), `name` is a display
 * label only — everything rendered comes from GET /reports/metrics, fetched
 * here so the page stands alone (no hub state, reloadable, bookmarkable).
 * The page auto-triggers window.print() once both requests settle; the
 * on-screen "Print" button is the fallback when the popup blocker or the user
 * cancels that first dialog.
 */

const ORG_NAME = "Thrive";
const RULE = "1px solid #d0d0d0";

const parseDate = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const printStyles = `
  .print-page {
    background: #fff;
    color: #000;
    font-family: Arial, Helvetica, "Segoe UI", sans-serif;
    font-size: 11px;
    line-height: 1.35;
    max-width: 8in;
    margin: 0 auto;
    padding: 24px;
  }
  .print-page h1 { font-size: 20px; margin: 0 0 2px; font-weight: 700; }
  .print-page h2 {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 22px 0 6px;
    padding-bottom: 3px;
    border-bottom: 2px solid #000;
  }
  .print-meta { color: #333; font-size: 11px; }
  .print-table { width: 100%; border-collapse: collapse; }
  .print-table th, .print-table td {
    border-bottom: ${RULE};
    padding: 3px 6px;
    text-align: left;
    vertical-align: top;
  }
  .print-table th { font-weight: 700; border-bottom: 1px solid #000; }
  .print-table td.num, .print-table th.num { text-align: right; white-space: nowrap; }
  .print-table tr, .print-section, .core-cell { break-inside: avoid; page-break-inside: avoid; }
  .print-table tr.section-row td {
    background: #eee;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.05em;
  }
  .core-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  .core-cell { border: ${RULE}; padding: 6px 8px; }
  .core-cell .core-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #333; }
  .core-cell .core-value { font-size: 18px; font-weight: 700; }
  .print-footer {
    margin-top: 24px;
    padding-top: 6px;
    border-top: ${RULE};
    font-size: 10px;
    color: #444;
  }
  .print-toolbar {
    max-width: 8in;
    margin: 12px auto 0;
    padding: 0 24px;
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .print-toolbar button {
    font: inherit;
    font-size: 13px;
    padding: 6px 14px;
    border: 1px solid #9D2362;
    background: #9D2362;
    color: #fff;
    border-radius: 4px;
    cursor: pointer;
  }
  .print-toolbar .secondary { background: #fff; color: #9D2362; }
  @media print {
    .no-print { display: none !important; }
    html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
    /* Any app chrome that might wrap this page. */
    header, nav, footer, .MuiAppBar-root, .MuiDrawer-root { display: none !important; }
    .print-page { max-width: none; padding: 0; font-size: 10.5px; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    @page { margin: 0.5in; }
  }
`;

const ConsultantPrintReport: React.FC = () => {
  const [params] = useSearchParams();
  const consultant = params.get("consultant") || "";
  const name = params.get("name") || "All consultants";
  const start = parseDate(params.get("start"));
  const end = parseDate(params.get("end"));

  const [generatedAt] = useState(() => dayjs());
  const printedRef = useRef(false);

  const totals = useMetrics({ start, end, consultant, groupBy: "none" });
  const monthly = useMetrics({ start, end, consultant, groupBy: "month" });

  const rangeLabel = useMemo(() => {
    if (!start && !end) return "All time";
    const left = start ? start.format("MMM D, YYYY") : "Beginning";
    const right = end ? end.format("MMM D, YYYY") : "Today";
    return `${left} – ${right}`;
  }, [start, end]);

  const catalogById = useMemo(
    () => new Map((totals.data?.catalog || []).map((e) => [e.id, e])),
    [totals.data],
  );
  const labelFor = (id: string) =>
    catalogById.get(id)?.label || CORE_METRIC_FALLBACK_LABELS[id] || id;
  const unitFor = (id: string) => catalogById.get(id)?.unit;

  const sections = useMemo(
    () => buildMetricSections(totals.data),
    [totals.data],
  );
  const monthGroups = monthly.data?.groups || [];

  const settled = !totals.loading && !monthly.loading;
  const failed = Boolean(totals.error && monthly.error);

  // Browser tab title doubles as the default "Save as PDF" filename.
  useEffect(() => {
    const previous = document.title;
    document.title = `${name} – ${ORG_NAME} Report (${rangeLabel})`;
    return () => {
      document.title = previous;
    };
  }, [name, rangeLabel]);

  // Auto-print once, after both requests settle. Tables only — nothing async
  // paints after this, so a short delay is enough for layout to flush.
  useEffect(() => {
    if (printedRef.current || !settled || failed) return;
    printedRef.current = true;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [settled, failed]);

  return (
    <>
      <style>{printStyles}</style>

      <div className="print-toolbar no-print">
        <button type="button" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => window.close()}
        >
          Close
        </button>
        {!settled && <span>Loading metrics…</span>}
      </div>

      <div className="print-page">
        <h1>{name}</h1>
        <div className="print-meta">
          {ORG_NAME} · Consultant Report
          <br />
          Reporting period: {rangeLabel}
          <br />
          Generated {generatedAt.format("MMM D, YYYY h:mm A")}
        </div>

        {totals.error && (
          <p className="print-meta">
            <strong>Core metrics unavailable:</strong> {totals.error}
          </p>
        )}

        {totals.data && (
          <div className="print-section">
            <h2>Core metrics</h2>
            <div className="core-grid">
              {CORE_METRIC_IDS.map((id) => (
                <div className="core-cell" key={id}>
                  <div className="core-label">{labelFor(id)}</div>
                  <div className="core-value">
                    {formatMetric(
                      metricNumber(totals.data?.total, id),
                      unitFor(id),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {sections.length > 0 && (
          <div className="print-section">
            <h2>All metrics</h2>
            <table className="print-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <React.Fragment key={section.title}>
                    <tr className="section-row">
                      <td colSpan={2}>{section.title}</td>
                    </tr>
                    {section.rows.map((row) => {
                      const value = bucketValue(totals.data?.total, row);
                      return (
                        <tr key={`${section.title}-${row.key}`}>
                          <td>{row.label}</td>
                          <td className="num">
                            {value === 0 ? "–" : formatMetric(value, row.unit)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="print-section">
          <h2>Monthly breakdown</h2>
          {monthly.error ? (
            <p className="print-meta">Monthly data unavailable: {monthly.error}</p>
          ) : monthGroups.length === 0 ? (
            <p className="print-meta">
              {monthly.loading
                ? "Loading…"
                : "No monthly activity in this period."}
            </p>
          ) : (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Month</th>
                  {CORE_METRIC_IDS.map((id) => (
                    <th className="num" key={id}>
                      {labelFor(id)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthGroups.map((group) => (
                  <tr key={group.key}>
                    <td>{group.label || group.key}</td>
                    {CORE_METRIC_IDS.map((id) => {
                      const value = metricNumber(group.metrics, id);
                      return (
                        <td className="num" key={id}>
                          {value === 0 ? "–" : formatMetric(value, unitFor(id))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {totals.data && (
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    {CORE_METRIC_IDS.map((id) => (
                      <td className="num" key={id}>
                        <strong>
                          {formatMetric(
                            metricNumber(totals.data?.total, id),
                            unitFor(id),
                          )}
                        </strong>
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="print-footer">
          {ORG_NAME} · {name} · {rangeLabel} · generated{" "}
          {generatedAt.format("MMM D, YYYY h:mm A")}
        </div>
      </div>
    </>
  );
};

export default ConsultantPrintReport;
